import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MDS_COLORS_LINE_BORDER } from '@rappi/mustache-b2b-foundations';
import { ArrowDownOutlineIcon, CrossIcon, FlexContainer, Loader, PlusIcon } from '@rappi/rests-commons-front';
import { lazy, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import EmptyState from '../../commons/empty-state/empty-state';
import { Header } from '../../commons/header/Header';
import { TableContainer } from '../../components/menu-generator-table/styles';
import { COMMERCIAL_PROCESSES_CONFIG } from '../../constants/commercial-processes-config';
import { countryByCode } from '../../constants/countries';
import { useGlobal } from '../../context/global/global.context';
import { GlobalActions } from '../../context/global/types';
import { useDebounce } from '../../hooks/use-debounce';
import { commercialProcessesService } from '../../services/commercial-processes.service';
import { observabilityService } from '../../services/observability';
import { formatDate } from '../../utils/format-date';
import { Container, Main } from '../commercial-process/styles';
import { customTableStyles, ReviewLink, Strikethrough } from './styles';
import './styles.css';
const Input = lazy(() => import(/* webpackChunkName: `Input` */ 'storesetupapp/components/Input'));
const Select = lazy(() => import(/* webpackChunkName: `Select` */ 'storesetupapp/components/Select'));
export const CommercialProcesses = () => {
    var _a, _b, _c, _d;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { state, dispatch } = useGlobal();
    const { country, auth } = state;
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [filterEmail, setFilterEmail] = useState((_b = (_a = auth === null || auth === void 0 ? void 0 : auth.userProfile) === null || _a === void 0 ? void 0 : _a.email) !== null && _b !== void 0 ? _b : '');
    const [filterProcessName, setFilterProcessName] = useState('');
    const [filterState, setFilterState] = useState('');
    const debouncedFilterEmail = useDebounce(filterEmail, 500);
    const { data, error, isLoading } = useSWR({
        key: 'commercial-processes',
        page,
        size,
        country,
        filters: {
            email: debouncedFilterEmail,
            processName: filterProcessName,
            status: filterState,
        },
    }, (params) => commercialProcessesService.getCommercialProcesses(params), {
        fallbackData: {
            content: [],
            totalElements: 0,
        },
        onError: (error) => {
            var _a, _b;
            dispatch({
                type: GlobalActions.showNotification,
                payload: { message: t('menuGenerator.table.notification.error'), type: 'error' },
            });
            observabilityService.captureError({
                error,
                name: 'commercial-processes',
                user: (_b = (_a = auth === null || auth === void 0 ? void 0 : auth.userProfile) === null || _a === void 0 ? void 0 : _a.email) !== null && _b !== void 0 ? _b : '',
            });
        },
    });
    const statusOptions = [
        { value: 'COMPLETED', label: t('commercialProcesses.state.completed') },
        { value: 'REJECTED', label: t('commercialProcesses.state.rejected') },
        { value: 'REVIEW', label: t('commercialProcesses.state.review') },
        { value: 'CANCELED', label: t('commercialProcesses.state.canceled') },
        { value: 'ERROR', label: t('commercialProcesses.state.error') },
        { value: 'ACTIVE', label: t('commercialProcesses.state.active') },
    ];
    const columns = [
        {
            name: t('commercialProcesses.table.columns.country'),
            selector: (row) => row.country,
            cell: (row) => _jsx("img", { src: countryByCode[row.country].flag }),
            sortable: true,
            wrap: true,
            maxWidth: '100px',
        },
        {
            name: t('commercialProcesses.table.columns.process'),
            selector: (row) => row.process_name,
            cell: (row) => row.process_name in COMMERCIAL_PROCESSES_CONFIG
                ? t(COMMERCIAL_PROCESSES_CONFIG[row.process_name].title, {
                    defaultValue: row.process_name,
                })
                : row.process_name,
            sortable: true,
            wrap: true,
        },
        {
            name: t('commercialProcesses.table.columns.agent'),
            selector: (row) => row.owner,
            sortable: true,
            wrap: true,
        },
        {
            name: t('commercialProcesses.table.columns.state'),
            selector: (row) => row.status,
            cell: (row) => {
                const levelByStatus = {
                    ACTIVE: 'medium',
                    COMPLETED: 'low',
                    REJECTED: 'high',
                    REVIEW: 'very-low',
                    CANCELED: 'high',
                    ERROR: 'high',
                };
                const labelByStatus = {
                    ACTIVE: t('commercialProcesses.state.active'),
                    COMPLETED: t('commercialProcesses.state.completed'),
                    REJECTED: t('commercialProcesses.state.rejected'),
                    REVIEW: t('commercialProcesses.state.review'),
                    CANCELED: t('commercialProcesses.state.canceled'),
                    ERROR: t('commercialProcesses.state.error'),
                };
                return _jsx(Strikethrough, { "$level": levelByStatus[row.status], children: labelByStatus[row.status] });
            },
            sortable: true,
            wrap: true,
        },
        {
            name: t('commercialProcesses.table.columns.startDate'),
            selector: (row) => row.created_at,
            cell: (row) => formatDate(row.created_at),
            sortable: true,
            wrap: true,
        },
        {
            name: t('commercialProcesses.table.columns.updateDate'),
            selector: (row) => row.updated_at,
            cell: (row) => formatDate(row.updated_at),
            sortable: true,
            wrap: true,
        },
        {
            name: t('commercialProcesses.table.columns.action'),
            cell: (row) => _jsx(ReviewLink, { to: `/hunter-tools/commercial-processes/${row.id}`, children: t('review') }),
            wrap: true,
            maxWidth: '100px',
        },
    ];
    return (_jsxs(Container, { children: [_jsx(Header, { title: t('commercialProcesses.title'), actions: [
                    {
                        text: t('commercialProcesses.startProcess'),
                        iconLeft: _jsx(PlusIcon, { size: 16, inheritColor: true }),
                        onClick: () => {
                            navigate('/hunter-tools/commercial-processes/finder');
                        },
                    },
                ] }), _jsx(Main, { children: _jsxs(FlexContainer, { direction: "column", style: { backgroundColor: '#FFFFFF', borderRadius: '8px', overflow: 'hidden' }, children: [_jsxs(FlexContainer, { gap: 24, justifyContent: "space-between", padding: "24px", style: { borderBottom: `1px solid ${MDS_COLORS_LINE_BORDER}` }, children: [_jsxs(FlexContainer, { gap: 8, children: [_jsx(Select, { autocomplete: true, name: "processName", options: Object.entries(COMMERCIAL_PROCESSES_CONFIG).map(([key, process]) => ({
                                                value: key,
                                                label: t(process.title),
                                            })), value: filterProcessName, setValue: (value) => {
                                                setFilterProcessName(value);
                                                setPage(1);
                                            }, label: t('commercialProcesses.filter.processName'), icon: filterProcessName ? _jsx(CrossIcon, { size: 16 }) : undefined, onClickIcon: () => {
                                                setFilterProcessName('');
                                                setPage(1);
                                            } }), _jsx(Select, { name: "state", options: statusOptions, value: filterState, setValue: (value) => {
                                                setFilterState(value);
                                                setPage(1);
                                            }, label: t('commercialProcesses.filter.state'), icon: filterState ? _jsx(CrossIcon, { size: 16 }) : undefined, onClickIcon: () => {
                                                setFilterState('');
                                                setPage(1);
                                            } })] }), _jsx(FlexContainer, { width: "100%", maxWidth: "320px", children: _jsx(Input, { fullWidth: true, name: "email", onChange: (e) => {
                                            const value = e.target.value;
                                            setFilterEmail(value);
                                            setPage(1);
                                        }, value: filterEmail, label: t('commercialProcesses.filter.email'), icon: filterEmail ? _jsx(CrossIcon, { size: 16 }) : undefined, onClickIcon: () => {
                                            setFilterEmail('');
                                            setPage(1);
                                        } }) })] }), _jsx(TableContainer, { children: _jsx(DataTable, { pagination: true, persistTableHead: true, paginationServer: true, columns: columns, data: (_c = data === null || data === void 0 ? void 0 : data.content) !== null && _c !== void 0 ? _c : [], customStyles: customTableStyles, progressPending: isLoading, noDataComponent: _jsx(EmptyState, { title: t(`commercialProcesses.table.${error ? 'error' : 'empty'}.label`), subtitle: t(`commercialProcesses.table.${error ? 'error' : 'empty'}.description`) }), paginationPerPage: size, paginationTotalRows: (_d = data === null || data === void 0 ? void 0 : data.totalElements) !== null && _d !== void 0 ? _d : 0, paginationDefaultPage: 1, paginationRowsPerPageOptions: [10, 20, 50], progressComponent: _jsx(Loader, { fullscreen: false }), paginationComponentOptions: {
                                    rangeSeparatorText: t('menuGenerator.table.pagination.rangeSeparatorText'),
                                    rowsPerPageText: t('menuGenerator.table.pagination.rowsPerPageText'),
                                    selectAllRowsItemText: t('menuGenerator.table.pagination.selectAllRowsItemText'),
                                }, onChangePage: (page) => {
                                    setPage(page);
                                }, onChangeRowsPerPage: (currentRowsPerPage) => {
                                    setSize(currentRowsPerPage);
                                }, sortIcon: _jsx(ArrowDownOutlineIcon, {}) }) })] }) })] }));
};
